/**
 * useUrlState - stato di vista (filtri, tab, ricerca, selezione) persistito nei
 * query param della URL invece che in useState locale.
 *
 * Regola UI HARD: tornando indietro da un dettaglio si deve ritrovare la lista
 * con gli stessi filtri/tab/selezione. React Router smonta il componente alla
 * navigazione (useState si perde), ma il back del browser ripristina la URL e
 * quindi i query param -> la vista precedente viene ricostruita.
 *
 * Uso:
 *   const [direzione, setDirezione] = useUrlState('direzione', 'tutte');
 *   const [year, setYear] = useUrlNumberState('anno', new Date().getFullYear());
 *
 * Nota: usa replace:true per non inquinare la history a ogni cambio filtro;
 * la nuova entry di history e' il dettaglio, cosi' il back torna alla lista
 * filtrata.
 */
import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

export function useUrlState(
  key: string,
  defaultValue: string,
): [string, (v: string) => void] {
  const [params, setParams] = useSearchParams();
  const value = params.get(key) ?? defaultValue;
  const setValue = useCallback(
    (v: string) => {
      // NON usare il functional updater di setSearchParams: in react-router 6
      // riceve i param del render corrente, non quelli dell'ultima navigate
      // nello stesso tick, quindi due set consecutivi nello stesso handler si
      // sovrascrivono a vicenda (bug cambio cartella email, 2026-07-11).
      // window.location.search e' aggiornato in modo sincrono da history.
      const next = new URLSearchParams(window.location.search);
      if (v === '' || v === defaultValue) next.delete(key);
      else next.set(key, v);
      setParams(next, { replace: true });
    },
    [key, defaultValue, setParams],
  );
  return [value, setValue];
}

export function useUrlNumberState(
  key: string,
  defaultValue: number,
): [number, (v: number) => void] {
  const [raw, setRaw] = useUrlState(key, String(defaultValue));
  const value = Number.isNaN(Number(raw)) ? defaultValue : Number(raw);
  const setValue = useCallback((v: number) => setRaw(String(v)), [setRaw]);
  return [value, setValue];
}

export function useUrlBoolState(
  key: string,
  defaultValue = false,
): [boolean, (v: boolean) => void] {
  const [raw, setRaw] = useUrlState(key, defaultValue ? '1' : '0');
  const value = raw === '1' || raw === 'true';
  const setValue = useCallback((v: boolean) => setRaw(v ? '1' : '0'), [setRaw]);
  return [value, setValue];
}
